import React from "react";
import { Languages } from "lucide-react";
import { useTranslation } from "react-i18next";
import { setAppLanguage, SUPPORTED_LANGUAGES } from "../i18n";

/**
 * Sélecteur FR / EN — styles explicites pour garantir la visibilité (navbar, formulaires).
 */
const LanguageSwitcher = ({
  className = "",
  compact = false,
  navbar = false,
  prominent = false,
  onDark = false,
  showIcon = false,
}) => {
  const { i18n, t } = useTranslation("common");
  const current = i18n.language?.startsWith("en") ? "en" : "fr";

  const isNavbar = navbar;
  const isCompact = compact && !isNavbar;

  return (
    <div
      className={`inline-flex shrink-0 items-center gap-1 rounded-full p-0.5 sm:p-1 ${
        isNavbar
          ? "border-2 border-omu-blue-500 bg-white shadow-md"
          : onDark
            ? "border border-white/40 bg-white/10"
            : prominent
              ? "border-2 border-omu-blue-300 bg-white shadow-md"
              : "border border-slate-300 bg-white shadow-sm"
      } ${className}`}
      style={isNavbar ? { minHeight: 40, zIndex: 60 } : undefined}
      role="group"
      aria-label={t("language")}
      data-testid="language-switcher"
    >
      {(showIcon || isNavbar) && (
        <span className="pl-2 text-omu-blue-500" aria-hidden>
          <Languages className="h-4 w-4 sm:h-[18px] sm:w-[18px]" />
        </span>
      )}
      {showIcon && !isNavbar && (
        <span className={`hidden sm:inline pr-0.5 text-sm font-medium ${onDark ? "text-white" : "text-slate-600"}`}>
          {t("language")}
        </span>
      )}
      {SUPPORTED_LANGUAGES.map((lang) => {
        const active = current === lang;
        const label = lang === "fr" ? t("french") : t("english");
        const short = lang.toUpperCase();
        const display = isNavbar || (isCompact && !prominent) ? short : label;

        return (
          <button
            key={lang}
            type="button"
            onClick={() => setAppLanguage(lang)}
            className={`rounded-full font-semibold transition-colors ${
              isNavbar
                ? "min-w-[2.75rem] px-3 py-1.5 text-sm"
                : prominent
                  ? "min-w-[2.75rem] px-3.5 py-1.5 text-sm"
                  : "min-w-[2rem] px-2.5 py-1 text-xs"
            } ${
              active
                ? onDark
                  ? "bg-white text-slate-900"
                  : "bg-omu-blue-500 text-white"
                : onDark
                  ? "text-white hover:bg-white/20"
                  : "text-slate-800 hover:bg-omu-blue-50"
            }`}
            style={
              active && !onDark
                ? { backgroundColor: "#0055FF", color: "#fff" }
                : undefined
            }
            aria-pressed={active}
            aria-label={label}
            title={label}
            data-testid={`lang-switch-${lang}`}
          >
            {display}
          </button>
        );
      })}
    </div>
  );
};

export default LanguageSwitcher;
